/* eslint-disable no-undef */
import CreditMeterButton from 'pages/credit-plans/CreditMeterButton';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { Stack, IconButton, Typography, Modal } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { GlobalContext } from '../../providers/GlobalProvider';
import { useWebSocketFetcher } from 'hooks/useWebSocketFetcher';
import { useLogAppAction } from 'hooks/useLogAppAction';
import RequiredLicense from 'pages/assign/RequiredLicense';
import TrialRunUsedPanel from 'components/Workflows/TrialRunUsedPanel';
import LeaveGuardDialog from 'components/Common/LeaveGuardDialog';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import AddPersonModal from 'components/Monitor/Modals/AddPersonModal';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import CasesAPI from 'api/CasesAPI';
import CreditsAPI from 'api/CreditsAPI';
import { EmailAPI } from 'api/EmailAPI';
import { exportDesignTicketReportPDF } from 'components/Common/EntityTicketPdf';
import { isSupportedImageFile } from 'components/ExportDesign/exportDesignAttachments';
import { AttachmentRef, uploadExportDesignAttachment } from 'components/ExportDesign/exportDesignUpload';
import { unansweredNextSteps } from 'components/ExportDesign/exportDesignNextSteps';
import {
  buildDesignTicketCase,
  buildShareUpdate,
  readDesignTicket,
} from 'components/ExportDesign/exportDesignTicketPayload';
import useLeaveGuard from 'hooks/useLeaveGuard';
import { useWorkflowCredits } from 'hooks/useWorkflowCredits';
import { LicenseType } from 'pages/assign/LicenseBadge';
import {
  WorkflowAssistantHeader,
  WorkflowCreditWarning,
  WorkflowPageHeader,
  WorkflowProgressHeader,
  WorkflowProgressTimeline,
  WorkflowStreamingResponse,
  useWorkflowChatScroll,
} from 'components/Workflows/StandardWorkflowLayout';
import ExportDesignReportCard, {
  DesignReportPayload,
} from 'components/ExportDesign/ExportDesignReportCard';
import ExportDesignPhaseCard from 'components/ExportDesign/ExportDesignPhaseCard';
import {
  buildExportDesignProgressSteps,
  classificationDetail,
  classificationSummaryHasContent,
  extractClassificationSummary,
  extractProductSpecs,
  humanizeKey,
  humanizeSpecRef,
  plainProgressText,
  productSpecsHaveContent,
  ClassificationSummary,
  ProductSpecsSnapshot,
} from 'components/ExportDesign/exportDesignProgress';
import { reasonPhrase } from 'components/ExportDesign/exportDesignControl';
import {
  restoreDesignSession,
  ResumeDesignTicketState,
} from 'components/ExportDesign/exportDesignResume';
import {
  chatSignature,
  serializeDesignChatTranscript,
} from 'components/ExportDesign/exportDesignChatTranscript';
import 'resources/styles/export-design.css';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;

const DESIGN_ASSISTANT_CREDIT_COST = 10;

// --- The Export Design Advisor chat. A leaner sibling of EARChatUI: a single free-form
// conversation (no fixed phase ORDER — the router dispatches per-turn to whichever
// capability the user asks for). It reuses the StandardWorkflowLayout primitives, the
// standard-workflow.css "Thinking" panel (phase → group → step → finding), the
// useWebSocketFetcher hook, and the Business+ license gate.
//
// It DOES share the same right-side WorkflowProgressTimeline panel EARChatUI/bite-import/
// BeneficialOwnershipWorkflow use — see exportDesignProgress.ts for how a free-form,
// non-linear conversation still maps onto the design brief's 5 fixed stages (each marked
// complete purely from whether workflow_state already has that stage's data, not from
// turn order).
//
// The composer itself is NOT the shared WorkflowComposer — it's a local fork (same pattern
// CustomsEntryAnalyzer.tsx uses for its own attach-a-PDF composer) so this tool can attach
// blueprint/spec PDFs and product photos without adding an attachments prop surface to the
// shared component before a second consumer (export_workflow / bite-import) actually needs it.

const MAX_PDF_BYTES = 40 * 1024 * 1024; // matches the pyapi-side cap
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const ACCEPTED_ATTACHMENT_TYPES =
  'application/pdf,.pdf,image/png,.png,image/jpeg,.jpg,.jpeg,image/webp,.webp';

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 KB';
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(kilobytes >= 10 ? 1 : 2)} KB`;
  }
  return `${(kilobytes / 1024).toFixed(2)} MB`;
};

const isImageFile = isSupportedImageFile;

// Same chunked arrayBuffer->btoa conversion Monitor/ResultsTab/Results.tsx uses to email a
// generated PDF blob — a plain btoa(String.fromCharCode(...bytes)) call overflows the call
// stack on anything but tiny PDFs.
const blobToBase64 = async (blob: Blob): Promise<string> => {
  const buffer = await blob.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

// A suggested follow-up rendered as a clickable chip below an answer. `label` is what the
// user reads; `prompt` is the message sent on click, so they never have to work out the
// phrasing themselves.
interface NextStep {
  label: string;
  prompt: string;
}

type ThoughtLevel = 'phase' | 'group' | 'step' | 'finding';
interface ThoughtStep {
  text: string;
  level: ThoughtLevel;
}
interface IMessage {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  // ISO time the message was created; kept with the saved chat so the ticket can show when each
  // message was sent.
  ts?: string;
  // Drawn by the chat itself from saved state (the "Reopened…" note on a continued ticket, a report
  // card rebuilt from the state), not said in the session — so it is not saved with the chat.
  ephemeral?: boolean;
  thoughtSteps?: ThoughtStep[];
  thoughtSeconds?: number;
  report?: DesignReportPayload;
  attachedFilenames?: string[];
  // Snapshot of the relevant workflow_state slice at the moment this turn completed —
  // rendered as its own collapsible chat card (ExportDesignPhaseCard), mirroring EARChatUI's
  // per-phase result cards. Only set on the turn that first produces/changes that data (see
  // the signature-dedupe refs in finalizeTurn) so the same card doesn't repeat every turn.
  productSpecsCard?: ProductSpecsSnapshot;
  keySpecsCard?: ClassificationSummary;
  // The "continue to <analysis>?" checkpoint question (see the backend's
  // _ensure_confirmed_baseline). Sent separately from `text` rather than appended to it, so it
  // renders AFTER the product-specs/key-specs-table cards below instead of above them.
  continuePrompt?: string;
  // Both arrive the same way and for the same reason: appended into the markdown they were
  // indistinguishable from the determination, and the disclaimer being last made it read as
  // the end of the turn. Rendered as their own elements below the answer.
  nextSteps?: NextStep[];
  disclaimer?: string;
}

const PROGRESS_EVENTS = new Set([
  'phase_started',
  'step_started',
  'step_completed',
  'step_finding',
]);

const formatThoughtDuration = (seconds: number | null): string =>
  !seconds || seconds < 2 ? 'Thought for a moment' : `Thought for ${seconds}s`;

const newId = (): string =>
  (window.crypto as any)?.randomUUID?.() ?? `design-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

// Order-independent signatures for the product-specs / key-specs dedupe check below —
// `specEntries`/`candidateUsml`/`bindingConstraints`/etc. are just echoed back from
// workflow_state each turn, and their order can shift turn-to-turn even when nothing about
// the underlying data actually changed (e.g. a new unrelated fact gets appended elsewhere in
// the same dict). Sorting the unordered collections before signing means a card only
// reappears on a genuine content change, not a harmless reordering.
const specsSignature = (snapshot: ProductSpecsSnapshot): string =>
  JSON.stringify({
    productName: snapshot.productName,
    productType: snapshot.productType,
    productDescription: snapshot.productDescription,
    specEntries: [...snapshot.specEntries].sort(([a], [b]) => a.localeCompare(b)),
  });

const keySpecsSignature = (summary: ClassificationSummary): string =>
  JSON.stringify({
    currentRegime: summary.currentRegime,
    candidateUsml: [...summary.candidateUsml].sort(),
    candidateEccns: [...summary.candidateEccns].sort(),
    bindingConstraints: summary.bindingConstraints
      .map((c) => JSON.stringify(c, Object.keys(c).sort()))
      .sort(),
  });

// What a save captures, as one string: the workflow state and the chat. The saved copy and the live
// session are compared through this, so a session restored from a ticket (see
// hydrateSavedDesignTicket) reads as unchanged until the conversation moves on.
const saveSignature = (workflowState: unknown, messages: unknown): string =>
  JSON.stringify({ workflowState: workflowState || {}, chat: chatSignature(messages) });

const ExportDesignChatUI: React.FC<{ userHasMonitor: boolean }> = ({
  userHasMonitor,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const location = useLocation<ResumeDesignTicketState>();
  const logAppAction = useLogAppAction();

  const {
    streamData,
    isStreaming,
    connectWebSocket,
    closeWebSocket,
    clearStreamData,
  } = useWebSocketFetcher(apiUrl);

  // Handoff → the forward Export Workflow (/export-page), via react-router location state.
  //
  // This used to send the product description ALONE, so everything the user had already told
  // this tool — end-use, destination, the ECCN it just worked out — was dropped and had to be
  // re-entered on the other side (reported in QA as "it doesn't include the End user
  // information I've already entered"). We now hand over the fact ledger too; EARChatUI maps
  // these onto its own fact names and seeds its workflow_state with them.
  //
  // Facts, not the report payload: `intended_use` / `destination_country` live in the ledger
  // (workflow_state.facts), not in DesignReportPayload.
  const handleSendToExportWorkflow = (productDescription: string) => {
    const seed = (productDescription || '').trim();
    if (!seed) {
      enqueueSnackbar('No product description to send yet.', { variant: 'warning' });
      return;
    }
    const factValue = (name: string): string | undefined => {
      const raw = (workflowStateRef.current?.facts as any)?.[name]?.value;
      const value = typeof raw === 'string' ? raw.trim() : raw ? String(raw) : '';
      return value || undefined;
    };
    // `candidate_eccns` is banked as a comma-joined string (see _bank_classification); the
    // forward workflow wants a single code, so take the first.
    const firstEccn = factValue('candidate_eccns')?.split(',')[0]?.trim();
    const seedFacts = {
      product_name: factValue('product_name'),
      product_description: factValue('product_description') || seed,
      end_use: factValue('intended_use'),
      destination_country: factValue('destination_country'),
      eccn: firstEccn,
    };
    enqueueSnackbar('Opening the Export Workflow with your product…', { variant: 'info' });
    history.push('/export-page', {
      seedProductDescription: seed,
      seedFacts: seedFacts,
    });
  };

  const [messages, setMessages] = useState<IMessage[]>([]);
  // Block starting a run the team can't cover (charged on completion). Re-read
  // the balance whenever the workflow returns to its start screen.
  const {
    insufficient: insufficientCredits,
    remaining: remainingCredits,
    refresh: refreshCredits,
    consumeTrialRun: consumeTrialRun,
  } = useWorkflowCredits(DESIGN_ASSISTANT_CREDIT_COST, 'design');
  const startBlockedByCredits = insufficientCredits && messages.length === 0;
  useEffect(() => {
    if (messages.length === 0) {
      refreshCredits();
    }
  }, [messages.length, refreshCredits]);
  // Set when the team's one trial run of Design Assistant has already been
  // spent (denial arrives as a workflow_error frame with data.reason ===
  // 'trial_limit_reached'). Replaces the chat/composer with TrialRunUsedPanel
  // for the rest of this mount -- see the "Gate AI workflows..." ticket.
  const [trialLimitReached, setTrialLimitReached] = useState(false);
  const [input, setInput] = useState('');
  const [thinkingElapsed, setThinkingElapsed] = useState(0);
  const [, setThoughtStepsVersion] = useState(0);
  // Reasoning blocks are expanded by default — this set tracks which ones the user has
  // manually COLLAPSED (inverted from a naive "expanded ids" set, so a brand-new thought
  // block needs no extra wiring to start open).
  const [collapsedThoughts, setCollapsedThoughts] = useState<Set<string>>(new Set());
  // Message ids whose answer text has finished its word-by-word reveal. The trailing block
  // (continue prompt, suggestion chips, disclaimer) waits for this: those are separate
  // elements from the streamed markdown, so without gating they all popped in at once while
  // the answer above them was still typing out.
  const [revealedTextIds, setRevealedTextIds] = useState<Set<string>>(new Set());

  // --- Save Result state. Mirrors EARChatUI's: a signature of the saved snapshot decides
  // whether there is unsaved work, rather than a flag that has to be set from every mutation
  // site (EARChatUI.tsx:976 getWorkflowSaveSignature).
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [newTicketName, setNewTicketName] = useState('');
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  const [frequency, setFrequency] = useState('Once');
  const [savingTicket, setSavingTicket] = useState(false);
  const [savedTicketId, setSavedTicketId] = useState<string>('');
  const [savedSignature, setSavedSignature] = useState('');
  // Track persisted recipients so the save wizard notifies only newly added teammates.
  const savedSharedWithRef = useRef<string[]>([]);
  // A successful save from the leave dialog must resume the blocked navigation.
  const continueAfterSaveRef = useRef(false);
  const leaveAfterSaveRef = useRef<(() => void) | null>(null);
  // --- Share (Final Design Summary's explicit Save-or-Share choice, per Thariq's email):
  // a second, visible action distinct from CreateTicketWizard's own silent "share with team"
  // sub-step — this one actually notifies the person it's shared with.
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  // Mirrors Ticket.tsx's triggerSendPdf: flips true while AddPersonModal's "Email PDF" action
  // is in flight, so the modal shows its spinner and blocks Cancel/close until it resolves.
  const [triggerSendPdf, setTriggerSendPdf] = useState(false);
  // The user's opening message, kept verbatim for the ticket's own record.
  const firstUserMessageRef = useRef<string>('');
  // Progress steps currently 'complete', so each logs one `_completed` app action as it
  // flips to complete (and again only if it was invalidated and re-completed).
  const completedStepsRef = useRef<Set<string>>(new Set());

  const [clfId, setClfId] = useState(() => newId());
  const workflowStateRef = useRef<Record<string, unknown>>({});
  const productDescRef = useRef<string>('');
  // Bumped whenever workflowStateRef mutates, so the right-side progress panel (which
  // reads the ref, not React state) recomputes — same ref+version-counter pattern
  // thoughtStepsRef/thoughtStepsVersion already uses below for the same reason.
  const [workflowStateVersion, setWorkflowStateVersion] = useState(0);
  const [currentDispatchStage, setCurrentDispatchStage] = useState<string | undefined>(undefined);

  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  // True while a saved ticket is being fetched to continue from. Typing or starting a new design in
  // that gap would be overwritten by the restore, so the composer waits for it. It starts true when the
  // chat is opened from a ticket, so the controls are already disabled on the very first render rather
  // than only once the effect that starts the fetch has run.
  const [isResuming, setIsResuming] = useState(Boolean(location.state?.resumeDesignTicketId));
  const resumeHydratedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const thoughtStepsRef = useRef<ThoughtStep[]>([]);
  const seenStepKeysRef = useRef<Set<string>>(new Set());
  const thinkingStartRef = useRef<number>(0);
  const lastProcessedChunkIndexRef = useRef(0);
  // Dedupe signatures for the product-specs / key-specs-table chat cards — a card is only
  // attached to a turn's message when the underlying snapshot actually changed since the
  // last time it was shown, so re-confirming the same specs on a later turn doesn't spam
  // a duplicate card into the conversation.
  const lastShownSpecsSigRef = useRef<string>('');
  const lastShownKeySpecsSigRef = useRef<string>('');
  // Bumped on every reset. `submit()` captures this at the start of a turn and re-checks it
  // after the (async) attachment upload gap — if a reset happened in the meantime, the
  // generation no longer matches and submit bails out instead of pushing messages / opening
  // a websocket for the conversation the user just cleared. Defense-in-depth alongside
  // disabling "New Design" during an upload (see disableNew below) — belt and suspenders,
  // since the disabled button closes the main path but this closes the race itself.
  const sessionGenerationRef = useRef(0);
  // Attachments that already finished uploading during a PRIOR failed submit attempt (a
  // partial Promise.allSettled failure) — carried over so a retry doesn't re-upload files
  // that already succeeded, and isn't lost if the user only needs to retry the failed ones.
  const pendingAttachmentsRef = useRef<AttachmentRef[]>([]);
  // One charge per design session (clfId): set when the final report is charged, cleared by
  // "New Design". Guards against the report frame being processed more than once.
  const hasSpentDesignCreditRef = useRef(false);

  // "New Design" — mirrors EARChatUI's resetEarSession: tear down the live socket and wipe
  // every piece of per-conversation state (including a fresh clfId, so the backend starts a
  // genuinely new workflow_state rather than continuing the old one).
  const resetExportDesignSession = () => {
    sessionGenerationRef.current += 1;
    closeWebSocket();
    clearStreamData();
    setMessages([]);
    setInput('');
    setThinkingElapsed(0);
    setThoughtStepsVersion(0);
    setCollapsedThoughts(new Set());
    setRevealedTextIds(new Set());
    setStagedFiles([]);
    pendingAttachmentsRef.current = [];
    hasSpentDesignCreditRef.current = false;
    setClfId(newId());
    workflowStateRef.current = {};
    firstUserMessageRef.current = '';
    completedStepsRef.current = new Set();
    setSavedTicketId('');
    setSavedSignature('');
    setNewTicketName('');
    setShareEmail([]);
    savedSharedWithRef.current = [];
    continueAfterSaveRef.current = false;
    productDescRef.current = '';
    setWorkflowStateVersion(0);
    setCurrentDispatchStage(undefined);
    thoughtStepsRef.current = [];
    seenStepKeysRef.current = new Set();
    lastProcessedChunkIndexRef.current = 0;
    lastShownSpecsSigRef.current = '';
    lastShownKeySpecsSigRef.current = '';
  };

  // --- Continue assessment. The saved ticket's button navigates here with `resumeDesignTicketId`
  // in router state (ExportDesignTicket's ContinueAssessmentButton) — the same hand-off the Export
  // Workflow uses (resumeExportTicket -> hydrateSavedExportTicket). The case is fetched rather than
  // carried in the state, so it is the stored copy and a failure can be reported.
  //
  // What is restored is where the assessment STANDS (state, opening request, a card per result), not
  // a replay of every turn. The next turn is sent with the restored state under this session's own
  // fresh conversation id, which the server treats as a cold start rebuilt from that state. The
  // ticket's name, sharing and frequency are restored too: an update REPLACES them on the server, so
  // starting from empty ones would silently un-share the ticket.
  const hydrateSavedDesignTicket = async (caseId: string) => {
    const generation = sessionGenerationRef.current;
    setIsResuming(true);
    try {
      const found = await CasesAPI.getCase(caseId);
      if (generation !== sessionGenerationRef.current) return; // the session was reset meanwhile
      const restored = found?.success && found.result ? restoreDesignSession(found.result) : null;
      if (!restored) {
        enqueueSnackbar(
          'Could not reopen that assessment — nothing was saved to continue from.',
          { variant: 'error' },
        );
        return;
      }

      workflowStateRef.current = restored.state;
      setWorkflowStateVersion((v) => v + 1);
      productDescRef.current = restored.initialRequest;
      firstUserMessageRef.current = restored.initialRequest;
      // Cards already on screen must not repeat on the next turn unless their data changed.
      lastShownSpecsSigRef.current = specsSignature(extractProductSpecs(restored.state));
      lastShownKeySpecsSigRef.current = keySpecsSignature(extractClassificationSummary(restored.state));
      setSavedTicketId(restored.ticketId);
      setNewTicketName(restored.ticketName);
      setShareEmail(restored.sharedWith);
      savedSharedWithRef.current = restored.sharedWith;
      setFrequency(restored.frequency);
      const restoredMessages = restored.messages.map((message) => ({ ...message, id: newId() }));
      setMessages(restoredMessages);
      // Nothing has changed yet, so the header offers "View Result" rather than an Update.
      setSavedSignature(saveSignature(restored.state, restoredMessages));
    } catch (error) {
      console.error('[Design Assistant] Failed to reopen the saved assessment:', error);
      enqueueSnackbar('Could not reopen that assessment. Please try again.', { variant: 'error' });
    } finally {
      setIsResuming(false);
    }
  };

  useEffect(() => {
    const caseId = location.state?.resumeDesignTicketId;
    if (caseId && !resumeHydratedRef.current) {
      resumeHydratedRef.current = true;
      hydrateSavedDesignTicket(caseId);
    }
  }, []);

  // --- Save Result.
  //
  // The wizard collects name/sharing/frequency, the payload is built by buildDesignTicketCase
  // (see exportDesignTicketPayload.ts for the contract), and it POSTs through the same
  // CasesAPI every other tool uses, into the Saved Work listing and the real ticket page —
  // no backend changes were needed for any of that (see exportDesignTicketPayload.ts).
  // A saved ticket named just "Design Assistant" is indistinguishable in Saved Work, so default to
  // the product being assessed (kept short), falling back to the tool's name.
  const defaultTicketName = (): string => {
    const specs = extractProductSpecs(workflowStateRef.current);
    const base = (specs.productName || specs.productType || '').trim();
    if (!base) return 'Design Assistant';
    return base.length > 60 ? `${base.slice(0, 57).trimEnd()}…` : base;
  };

  const handleSaveResult = () => {
    if (!hasSavableWork) {
      enqueueSnackbar('Run an assessment before saving.', { variant: 'info' });
      return;
    }
    logAppAction('design_assistant:save_results');
    setNewTicketName(newTicketName || defaultTicketName());
    setIsSaveModalOpen(true);
  };

  // User-initiated "New Design" — resetExportDesignSession is the reset itself.
  const startOver = () => {
    logAppAction('design_assistant:start_over');
    resetExportDesignSession();
  };

  // Returns the saved case id on success (undefined on failure, having already shown the error
  // toast) — handleShareResult needs the id immediately, before this component's own
  // savedTicketId state has re-rendered, so it can't just read that state right after calling
  // this.
  const saveDesignTicket = async (name: string): Promise<string | undefined> => {
    const failSave = (reason: unknown): undefined => {
      console.error('[Design Assistant] Failed to save assessment ticket:', reason);
      enqueueSnackbar(
        'Could not save this assessment. Please try again, and tell the BITE team if it keeps failing.',
        { variant: 'error' },
      );
      return undefined;
    };

    setSavingTicket(true);
    // try/finally so a rejected request can't leave savingTicket stuck true. The modal only
    // closes on success, so a failed save keeps the entered name/sharing/frequency for a retry.
    try {
      const query = buildDesignTicketCase({
        existingId: savedTicketId || undefined,
        ticketName: name,
        workflowState: workflowStateRef.current || {},
        sharedWith: shareEmail,
        frequency: frequency,
        curUser: cur_user,
        initialRequest: firstUserMessageRef.current,
        chatTranscript: serializeDesignChatTranscript(messages),
      });
      const priorShared = new Set(savedSharedWithRef.current.map((email) => email.toLowerCase()));
      const newlyShared = shareEmail.filter((email, index) =>
        Boolean(email) && !priorShared.has(email.toLowerCase()) &&
        shareEmail.findIndex((candidate) => candidate.toLowerCase() === email.toLowerCase()) === index,
      );

      const resp = savedTicketId
        ? await CasesAPI.updateCase(query)
        : await CasesAPI.createCase({ newCase: query });

      if (resp?.success) {
        const id = resp.result?._id || savedTicketId;
        setSavedTicketId(id);
        savedSharedWithRef.current = shareEmail;
        // Clears the leave guard — there is now a saved copy to come back to.
        setSavedSignature(currentSignature);
        setIsSaveModalOpen(false);
        enqueueSnackbar(
          savedTicketId ? 'Updated the saved assessment' : 'Saved this assessment',
          { variant: 'success' },
        );
        newlyShared.forEach((email) => {
          // Notification is best-effort; the assessment has already been saved and shared.
          handleShareTicketNotification(name, cur_user, email, id);
        });
        if (continueAfterSaveRef.current) {
          continueAfterSaveRef.current = false;
          leaveAfterSaveRef.current?.();
        }
        return id;
      }
      return failSave(resp);
    } catch (error) {
      return failSave(error);
    } finally {
      setSavingTicket(false);
    }
  };

  // --- Share Result. A second, explicit action alongside Save (Thariq's email: "ask user if
  // they want to 1) Save or 2) Share — Share saves the ticket to saved work AND shares it with
  // a member of the team"). Reuses the same live Share pattern already on the Monitor/Ticket
  // pages (AddPersonModal + Case.sharedWith + handleShareTicketNotification) rather than
  // inventing a new one — distinct from CreateTicketWizard's own, unrelated "share with team"
  // sub-step, which silently sets sharedWith at save time with no notification.
  const handleShareResult = async () => {
    if (!hasSavableWork) {
      enqueueSnackbar('Run an assessment before sharing.', { variant: 'info' });
      return;
    }
    // Save first when there is no ticket yet, or when the conversation has moved on since the last
    // save — otherwise Share would send the teammate a stale copy.
    if (!savedTicketId || hasUnsavedWork) {
      const name = newTicketName || defaultTicketName();
      setNewTicketName(name);
      const savedId = await saveDesignTicket(name);
      if (!savedId) return; // saveDesignTicket already surfaced the error toast
    }
    setIsShareModalOpen(true);
  };

  const handleAddShared = async (emails: string[]) => {
    if (!savedTicketId) return; // handleShareResult guarantees a save happened first
    try {
      // Build the update from the case as it is stored now: the server REPLACES `activity` and
      // `sharedWith` with what is sent, so a local mirror (which would start empty) would erase the
      // ticket's earlier history. If the case can't be read, stop rather than overwrite blind.
      const current = await CasesAPI.getCase(savedTicketId);
      if (!current?.success || !current.result) {
        enqueueSnackbar('Error sharing.', { variant: 'error' });
        return;
      }
      const update = buildShareUpdate({
        savedCase: current.result,
        emails: emails,
        actor: {
          kc_id: cur_user?.basics?.id,
          email: cur_user?.basics?.email,
          firstName: cur_user?.basics?.firstName,
          lastName: cur_user?.basics?.lastName,
        },
      });
      if (!update) {
        enqueueSnackbar('This ticket is already shared with the selected user(s).', {
          variant: 'info',
        });
        return;
      }
      const res = await CasesAPI.updateCase({
        _id: savedTicketId,
        sharedWith: update.sharedWith,
        activity: update.activity,
      });
      if (!res?.success) {
        enqueueSnackbar('Error sharing.', { variant: 'error' });
        return;
      }
      setShareEmail(update.sharedWith);
      savedSharedWithRef.current = update.sharedWith;
      update.newEmails.forEach((email: string) => {
        handleShareTicketNotification(
          newTicketName || 'Design Assistant', cur_user, email, savedTicketId,
        );
      });
      enqueueSnackbar('Successfully shared.', { variant: 'success' });
    } catch (error) {
      console.error('[Design Assistant] Failed to share assessment ticket:', error);
      enqueueSnackbar('Error sharing.', { variant: 'error' });
    }
  };

  // --- "Email PDF" (AddPersonModal's `send` mode, same footer button and component Monitor's
  // Ticket page uses). Generates the exact PDF "Download PDF" produces on the saved ticket page
  // (exportDesignTicketReportPDF fed by readDesignTicket), so the emailed file always matches
  // Saved Work — not a separate render of the live, in-progress conversation.
  const handleSendDesignPdf = async (payload: { name: string; emails: string[] }) => {
    if (payload.emails.length === 0) {
      enqueueSnackbar('Please add at least one recipient email', { variant: 'error' });
      return;
    }

    setTriggerSendPdf(true);
    try {
      // Same guard as handleShareResult: an email built off a stale or nonexistent saved copy
      // would mismatch what View Result/Saved Work shows, so save first when needed.
      let ticketId = savedTicketId;
      if (!ticketId || hasUnsavedWork) {
        const name = newTicketName || defaultTicketName();
        setNewTicketName(name);
        const savedId = await saveDesignTicket(name);
        if (!savedId) return; // saveDesignTicket already surfaced the error toast
        ticketId = savedId;
      }

      const current = await CasesAPI.getCase(ticketId);
      if (!current?.success || !current.result) {
        throw new Error('Could not load the saved assessment');
      }

      const ticket = readDesignTicket(current.result);
      const resolvedFileName = `${payload.name || ticket.ticketName || 'Design Assistant'}.pdf`;
      const pdfBlob = await exportDesignTicketReportPDF(ticket, resolvedFileName, {
        shouldDownload: false,
      });
      const pdfBase64 = await blobToBase64(pdfBlob);
      const senderName = `${cur_user?.basics?.firstName || ''} ${
        cur_user?.basics?.lastName || ''
      }`.trim();

      const emailRes = await EmailAPI.sendTariffResultPdf({
        recipients: payload.emails,
        fileName: resolvedFileName,
        pdfBase64: pdfBase64,
        docType: 'Design Assistant',
        ticketId: ticketId,
        ticketName: ticket.ticketName,
        senderName: senderName,
        senderEmail: cur_user?.basics?.email,
      });

      if (!emailRes?.success) {
        throw new Error(emailRes?.result || 'Failed to send PDF email');
      }

      enqueueSnackbar('PDF emailed successfully', { variant: 'success' });
      setIsShareModalOpen(false);
    } catch (error: any) {
      console.error('[Design Assistant] Failed to send result PDF:', error);
      enqueueSnackbar(`Failed to send result PDF: ${error?.message || 'Unknown error'}`, {
        variant: 'error',
      });
    } finally {
      setTriggerSendPdf(false);
    }
  };

  // Grow the composer with a long description, up to the stylesheet's max-height, then scroll —
  // the same autosize the shared WorkflowComposer (Export Workflow) does. This composer is a
  // local fork (for attachments), so it doesn't inherit that behavior and has to repeat it.
  // A placeholder that wraps grows the box to show it in full, exactly as it does in the Export
  // Workflow (scrollHeight counts the wrapped placeholder) — nothing is clipped or shortened.
  // The measurement depends on the box's width as much as on the text: a placeholder that wrapped
  // when the pane was narrow must shrink back to one line when it widens, so it is also re-run
  // whenever the textarea's width changes (see the observer below), not just when the input does.
  const resizeComposer = useCallback(() => {
    const textarea = composerTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = '34px';
    const computedMaxHeight = Number.parseFloat(window.getComputedStyle(textarea).maxHeight);
    const maxHeight = Number.isFinite(computedMaxHeight) ? computedMaxHeight : 120;
    textarea.style.height = `${Math.max(34, Math.min(textarea.scrollHeight, maxHeight))}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight + 1 ? 'auto' : 'hidden';
  }, []);

  useLayoutEffect(() => {
    resizeComposer();
  }, [input, resizeComposer]);

  // The composer is rendered in two places (empty state, then the conversation footer), so the
  // textarea remounts when the first message is sent; measure it on setup and observe it from there.
  const composerInEmptyState = messages.length === 0;
  useEffect(() => {
    const textarea = composerTextareaRef.current;
    if (!textarea) return undefined;
    resizeComposer();
    if (typeof ResizeObserver === 'undefined') return undefined;
    let lastWidth = textarea.getBoundingClientRect().width;
    // Width only: this effect changes the height itself, so reacting to height would loop.
    const observer = new ResizeObserver(() => {
      const width = textarea.getBoundingClientRect().width;
      if (Math.abs(width - lastWidth) < 1) return;
      lastWidth = width;
      resizeComposer();
    });
    observer.observe(textarea);
    return () => observer.disconnect();
  }, [resizeComposer, composerInEmptyState]);

  const chatScrollRef = useWorkflowChatScroll(messages.length, messages);

  // Live "Thinking for Ns" ticker while the socket streams.
  useEffect(() => {
    if (!isStreaming) return undefined;
    const id = window.setInterval(() => {
      setThinkingElapsed(
        Math.max(0, Math.round((Date.now() - thinkingStartRef.current) / 1000)),
      );
    }, 1000);
    return () => window.clearInterval(id);
  }, [isStreaming]);

  // Finalise the turn onto the last bot message when a turn's answer lands. A report turn also
  // carries the structured `report` payload, rendered as a rich card instead of the markdown.
  interface TurnExtras {
    continuePrompt?: string;
    nextSteps?: NextStep[];
    disclaimer?: string;
  }

  const finalizeTurn = (
    text: string,
    report?: DesignReportPayload,
    extras: TurnExtras = {},
  ) => {
    const captured = thoughtStepsRef.current;
    const seconds = Math.max(
      0,
      Math.round((Date.now() - thinkingStartRef.current) / 1000),
    );

    const specsSnapshot = extractProductSpecs(workflowStateRef.current);
    const specsSig = specsSignature(specsSnapshot);
    const showSpecsCard =
      productSpecsHaveContent(specsSnapshot) && specsSig !== lastShownSpecsSigRef.current;
    if (showSpecsCard) lastShownSpecsSigRef.current = specsSig;

    const keySpecsSnapshot = extractClassificationSummary(workflowStateRef.current);
    const keySpecsSig = keySpecsSignature(keySpecsSnapshot);
    const showKeySpecsCard =
      classificationSummaryHasContent(keySpecsSnapshot) &&
      keySpecsSig !== lastShownKeySpecsSigRef.current;
    if (showKeySpecsCard) lastShownKeySpecsSigRef.current = keySpecsSig;

    setMessages((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i -= 1) {
        if (next[i].sender === 'bot' && next[i].text === 'Thinking...') {
          next[i] = {
            ...next[i],
            text: text,
            ts: new Date().toISOString(),
            thoughtSteps: captured.length ? captured : undefined,
            thoughtSeconds: seconds,
            report: report,
            productSpecsCard: showSpecsCard ? specsSnapshot : undefined,
            keySpecsCard: showKeySpecsCard ? keySpecsSnapshot : undefined,
            continuePrompt: extras.continuePrompt,
            nextSteps: extras.nextSteps,
            disclaimer: extras.disclaimer,
          };
          return next;
        }
      }
      next.push({
        id: newId(),
        sender: 'bot',
        text: text,
        ts: new Date().toISOString(),
        report: report,
        productSpecsCard: showSpecsCard ? specsSnapshot : undefined,
        keySpecsCard: showKeySpecsCard ? keySpecsSnapshot : undefined,
        continuePrompt: extras.continuePrompt,
        nextSteps: extras.nextSteps,
        disclaimer: extras.disclaimer,
      });
      return next;
    });
  };

  // Charged when the final report is delivered (not on start), so an interrupted or errored
  // run costs nothing. Fire-and-forget: a billing failure must not hide the finished report.
  // Mirrors EARChatUI.spendExportWorkflowCompletionCredit's trial-vs-paid pool choice.
  const spendDesignCompletionCredit = async () => {
    const teamId = cur_user?.selected_team?.id;
    if (!teamId) return;
    // The team's free trial run: nothing to charge.
    if (consumeTrialRun()) return;
    try {
      const actor = {
        userId: cur_user?.basics?.id || null,
        userName:
          [cur_user?.basics?.firstName, cur_user?.basics?.lastName].filter(Boolean).join(' ') ||
          null,
        userEmail: cur_user?.basics?.email || null,
      };
      // The unified spend chooses trial only when it can cover the complete
      // charge, then falls back to paid credits without splitting one run.
      const response = await CreditsAPI.spendCredits({
        teamId: teamId,
        source: 'design_assistant',
        referenceId: clfId,
        ...actor,
      });
      if (!response?.success) {
        console.error('[Design Assistant] Failed to log credit spend', response);
      }
    } catch (error) {
      console.error('[Design Assistant] Failed to log credit spend', error);
    }
  };

  // Process newly-arrived stream chunks (progress → Thinking panel; sticky → messages).
  useEffect(() => {
    if (streamData.length <= lastProcessedChunkIndexRef.current) return;
    const newChunks = streamData.slice(lastProcessedChunkIndexRef.current);
    lastProcessedChunkIndexRef.current = streamData.length;

    for (const raw of newChunks) {
      const lines = String(raw)
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      for (const line of lines) {
        let frame: any = null;
        try {
          frame = JSON.parse(line);
        } catch {
          continue;
        }

        const event: string | undefined = frame.event;
        const progressText: string | undefined =
          frame.progress_message ?? undefined;

        if (frame.workflow_state) {
          workflowStateRef.current = frame.workflow_state;
          setWorkflowStateVersion((v) => v + 1);
        }
        if (frame.stage) setCurrentDispatchStage(frame.stage);

        if (event && PROGRESS_EVENTS.has(event) && progressText) {
          const trimmed = plainProgressText(progressText);
          const last = thoughtStepsRef.current[thoughtStepsRef.current.length - 1];
          if (trimmed && last?.text !== trimmed) {
            let level: ThoughtLevel = 'step';
            if (event === 'phase_started') {
              level = 'phase';
            } else if (event === 'step_finding') {
              level = 'finding';
            } else if (
              frame.current_step &&
              !seenStepKeysRef.current.has(frame.current_step)
            ) {
              seenStepKeysRef.current.add(frame.current_step);
              level = 'group';
            }
            thoughtStepsRef.current = [
              ...thoughtStepsRef.current,
              { text: trimmed, level: level },
            ];
            setThoughtStepsVersion((v) => v + 1);
          }
          continue;
        }

        if (event === 'workflow_error' && frame.data?.reason === 'trial_limit_reached') {
          // Replaces the whole chat/composer area with TrialRunUsedPanel
          // instead of a chat bubble -- see the panel's render branch below.
          setTrialLimitReached(true);
          continue;
        }

        // Sticky content: the turn's answer / clarifying question / error. A report frame carries
        // the structured `report` payload → render the rich card instead of the markdown.
        const extras = {
          continuePrompt: frame.data?.continue_prompt as string | undefined,
          nextSteps: frame.data?.next_steps as NextStep[] | undefined,
          disclaimer: frame.data?.disclaimer as string | undefined,
        };
        if (event === 'final' && frame.report && !hasSpentDesignCreditRef.current) {
          hasSpentDesignCreditRef.current = true;
          spendDesignCompletionCredit();
        }
        if (event === 'final' || event === 'followup' || event === 'workflow_error') {
          finalizeTurn(frame.message || '…', frame.report as DesignReportPayload | undefined, extras);
        } else if (frame.message) {
          finalizeTurn(frame.message, frame.report as DesignReportPayload | undefined, extras);
        }
      }
    }
  }, [streamData]);

  const handleFiles = (files: File[]) => {
    for (const file of files) {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isImage = isImageFile(file);
      if (!isPdf && !isImage) {
        enqueueSnackbar(`${file.name}: only PDF or image (PNG/JPEG/WebP) attachments are supported.`, {
          variant: 'warning',
        });
        continue;
      }
      const maxBytes = isPdf ? MAX_PDF_BYTES : MAX_IMAGE_BYTES;
      if (file.size > maxBytes) {
        enqueueSnackbar(`${file.name} is too large (max ${formatFileSize(maxBytes)}).`, {
          variant: 'warning',
        });
        continue;
      }
      setStagedFiles((prev) => [...prev, file]);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(Array.from(event.target.files || []));
    event.target.value = '';
  };

  const handleRemoveStagedFile = (index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const submit = async (text: string) => {
    const trimmed = text.trim();
    if (
      (!trimmed && stagedFiles.length === 0) ||
      !userHasMonitor ||
      isStreaming ||
      isUploadingAttachments ||
      isResuming ||
      startBlockedByCredits
    ) {
      return;
    }

    const startGeneration = sessionGenerationRef.current;
    const filesToUpload = stagedFiles;

    if (filesToUpload.length > 0) {
      // The upload is made for a team and checked against it, so there has to be one.
      const groupId = cur_user?.selected_team?.id;
      if (!groupId) {
        enqueueSnackbar('Choose a team before attaching files.', { variant: 'error' });
        return;
      }
      setIsUploadingAttachments(true);
      const settled = await Promise.allSettled(
        filesToUpload.map((file) => uploadExportDesignAttachment(file, clfId, groupId)),
      );
      // A "New Design" reset happened while these uploads were in flight — bail out
      // entirely rather than push this turn's message / open a websocket against the
      // conversation the user already cleared (New Design is also disabled during
      // upload, so this is a defense-in-depth check, not the primary guard).
      if (sessionGenerationRef.current !== startGeneration) {
        setIsUploadingAttachments(false);
        return;
      }

      const succeeded: AttachmentRef[] = [];
      const failedFiles: File[] = [];
      // Keep the server's own reason. uploadExportDesignAttachment already unwraps the
      // API's `detail`, and dropping it here left a QA tester — and the console — with
      // nothing but a generic "failed to upload", so the actual cause was only
      // recoverable from server logs.
      const failureReasons: string[] = [];
      settled.forEach((outcome, i) => {
        if (outcome.status === 'fulfilled') {
          succeeded.push(outcome.value);
        } else {
          failedFiles.push(filesToUpload[i]);
          const reason =
            outcome.reason instanceof Error
              ? outcome.reason.message
              : String(outcome.reason ?? '');
          if (reason) failureReasons.push(reason);
        }
      });
      // Keep whatever succeeded so a retry doesn't re-upload it — only the failed files
      // need to go through the composer again.
      pendingAttachmentsRef.current = [...pendingAttachmentsRef.current, ...succeeded];
      setIsUploadingAttachments(false);

      if (failedFiles.length > 0) {
        setStagedFiles(failedFiles);
        const subject =
          failedFiles.length === 1
            ? `${failedFiles[0].name} failed to upload`
            : `${failedFiles.length} files failed to upload`;
        // The server's reason is the actionable half — "is over the 40000000 byte limit"
        // or "Unsupported attachment type" tells the user what to do; a bare "try again"
        // does not, and for a 500 it at least gives QA something concrete to report.
        const reason = failureReasons[0];
        enqueueSnackbar(reason ? `${subject}: ${reason}` : `${subject}. Please try again.`, {
          variant: 'error',
        });
        failureReasons.forEach((r) => console.error('[Design Assistant] attachment upload:', r));
        return;
      }
      setStagedFiles([]);
    }

    const attachments = pendingAttachmentsRef.current;
    pendingAttachmentsRef.current = [];

    // Based on `attachments` (all accumulated successes), not `filesToUpload` (just this
    // call's batch) — if an earlier attempt partially failed, files that succeeded THEN are
    // included here too, even though they aren't part of this retry's filesToUpload.
    const attachedSummary =
      attachments.length === 1
        ? `Attached ${attachments[0].filename}`
        : `Attached ${attachments.length} files`;
    // A turn with only attachments and no typed text still needs a non-empty `message` —
    // the workflow falls back to product_desc when message is blank, which would ignore
    // the attachment context entirely on the very first turn.
    const messageToSend = trimmed || `Please review the attached document(s). ${attachedSummary}.`;
    const displayText = trimmed || attachedSummary;
    if (!productDescRef.current) productDescRef.current = messageToSend;
    // Kept verbatim for the saved ticket's own record, the way the export ticket keeps
    // initialRequest — the fact ledger's product_description is the model's tidied version.
    if (!firstUserMessageRef.current) {
      logAppAction('design_assistant:started');
      firstUserMessageRef.current = messageToSend;
    }

    setMessages((prev) => [
      ...prev,
      {
        id: newId(),
        sender: 'user',
        text: displayText,
        attachedFilenames: attachments.length > 0 ? attachments.map((a) => a.filename) : undefined,
        ts: new Date().toISOString(),
      },
      { id: newId(), sender: 'bot', text: 'Thinking...' },
    ]);
    thoughtStepsRef.current = [];
    seenStepKeysRef.current = new Set();
    thinkingStartRef.current = Date.now();
    setThinkingElapsed(0);
    clearStreamData();
    lastProcessedChunkIndexRef.current = 0;

    connectWebSocket(
      '/ws/export_design',
      {
        clf_id: clfId,
        product_desc: productDescRef.current,
        message: messageToSend,
        workflow_state: workflowStateRef.current,
        attachments: attachments,
        group_id: cur_user?.selected_team?.id,
      },
      { sendAccessToken: true },
    );
    setInput('');
  };

  const toggleThought = (id: string) =>
    setCollapsedThoughts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const renderThoughtSteps = (steps: ThoughtStep[], keyPrefix: string) =>
    steps.map((step, i) => {
      if (step.level === 'phase') {
        return (
          <div
            className='standard-workflow-thought-goal'
            key={`${keyPrefix}-${i}`}
          >
            {step.text}
          </div>
        );
      }
      if (step.level === 'group') {
        return (
          <div
            className='standard-workflow-thought-group'
            key={`${keyPrefix}-${i}`}
          >
            {step.text}
          </div>
        );
      }
      if (step.level === 'finding') {
        return (
          <div
            className='standard-workflow-thought-step standard-workflow-thought-finding'
            key={`${keyPrefix}-${i}`}
          >
            <span aria-hidden='true' />
            <div>{step.text}</div>
          </div>
        );
      }
      return (
        <div
          className='standard-workflow-thought-step'
          key={`${keyPrefix}-${i}`}
        >
          <span aria-hidden='true' />
          <div>{step.text}</div>
        </div>
      );
    });


  const buildProductSpecsCardProps = (snapshot: ProductSpecsSnapshot) => ({
    title: 'Product Specs',
    complete: true,
    collapsedSummary:
      [snapshot.productName || snapshot.productType, snapshot.productDescription]
        .filter(Boolean)
        .join(' — ') || 'Awaiting information...',
    sections: [
      {
        title: 'Product',
        rows: [
          ...(snapshot.productName ? [{ label: 'Product Name', value: snapshot.productName }] : []),
          ...(snapshot.productType ? [{ label: 'Product Type', value: snapshot.productType }] : []),
          ...(snapshot.productDescription
            ? [{ label: 'Description', value: snapshot.productDescription }]
            : []),
        ],
      },
      ...(snapshot.specEntries.length > 0
        ? [
            {
              title: 'Specifications',
              rows: snapshot.specEntries.map(([key, value]) => ({
                label: humanizeKey(key),
                value: value,
              })),
            },
          ]
        : []),
    ],
  });

  const buildKeySpecsCardProps = (summary: ClassificationSummary) => ({
    title: 'Key Specs Table',
    complete: true,
    collapsedSummary:
      [
        classificationDetail(summary),
        summary.bindingConstraints.length > 0
          ? `${summary.bindingConstraints.length} binding constraint(s)`
          : undefined,
      ]
        .filter(Boolean)
        .join(' — ') || 'Awaiting information...',
    sections: [
      {
        title: 'Classification',
        rows: [
          // The control and what it is controlled for lead; the regime is the label beside them.
          ...(summary.primaryControl
            ? [{ label: 'Control', value: summary.primaryControl }]
            : []),
          ...(summary.reasonsForControl.length
            ? [{ label: 'Controlled for', value: reasonPhrase(summary.reasonsForControl) }]
            : []),
          ...(summary.currentRegime
            ? [{ label: 'Regime', value: summary.currentRegime }]
            : []),
          ...(summary.candidateUsml.length
            ? [{ label: 'USML', value: summary.candidateUsml.join(', ') }]
            : []),
          ...(summary.candidateEccns.length
            ? [{ label: 'ECCN', value: summary.candidateEccns.join(', ') }]
            : []),
        ],
      },
      ...(summary.bindingConstraints.length > 0
        ? [
            {
              title: 'Binding constraints',
              rows: summary.bindingConstraints.map((bc) => ({
                label: bc.citation || 'Control',
                value: bc.matched_spec
                  ? humanizeSpecRef(bc.matched_spec)
                  : bc.decontrol_threshold || '—',
              })),
            },
          ]
        : []),
    ],
  });

  // Depends on workflowStateVersion (a counter bumped whenever workflowStateRef.current
  // mutates) rather than the ref itself, since refs aren't valid useMemo dependencies.
  const progressSteps = useMemo(
    () => buildExportDesignProgressSteps(workflowStateRef.current, currentDispatchStage, isStreaming),
    [workflowStateVersion, currentDispatchStage, isStreaming],
  );

  // The panel's four steps, in the order buildExportDesignProgressSteps returns them. They
  // are state-driven, so only a step that actually reached 'complete' is logged (an
  // unanswered or not-yet-requested step stays 'pending').
  useEffect(() => {
    const STEP_KEYS = ['product_specs', 'classify_export', 'specification_map', 'final_summary'];
    progressSteps.forEach((step, i) => {
      const key = STEP_KEYS[i];
      if (!key) return;
      if (step.status === 'complete') {
        if (!completedStepsRef.current.has(key)) {
          completedStepsRef.current.add(key);
          logAppAction(`design_assistant:${key}_completed`);
        }
      } else {
        completedStepsRef.current.delete(key);
      }
    });
  }, [progressSteps, logAppAction]);

  // Signature of what a save would capture. Same dependency as progressSteps: workflowStateRef
  // is a ref, so the version counter is what makes this recompute. A save also persists the
  // raw stream (buildDesignTicketCase's aiStream), so assistant output that doesn't touch the
  // workflow state still has to count as unsaved. The stream is append-only until
  // clearStreamData(), so its length stands in for its contents — stringifying the whole
  // stream here would re-serialize it on every incoming chunk.
  const currentSignature = useMemo(
    () => saveSignature(workflowStateRef.current, messages),
    [workflowStateVersion, messages],
  );
  const hasSavableWork = messages.length > 0 && !isStreaming;
  const hasUnsavedWork = messages.length > 0 && currentSignature !== savedSignature;
  // One definition for the header's and the report card's Save / Share, so they cannot drift apart:
  // a user without Business access, an empty conversation, a save already in flight and an
  // exhausted trial all block both. (Save is also inert when nothing has changed since the last one.)
  const shareBlocked = !userHasMonitor || !hasSavableWork || savingTicket || trialLimitReached;
  const saveBlocked = shareBlocked || !hasUnsavedWork;
  // Share belongs to the last stage (the Final Design Summary), so the header only offers it once
  // a summary exists; Save stays available throughout.
  const hasReport = useMemo(
    () => Boolean((workflowStateRef.current as { report?: unknown } | undefined)?.report),
    [workflowStateVersion],
  );
  // Once a ticket is saved and nothing has changed since, the header (and the report card) point at
  // it instead of offering Save again — the same rule the Classify workflow uses. As soon as the
  // conversation changes this goes undefined and the Update button returns.
  const savedResultPath =
    savedTicketId && !hasUnsavedWork ? `/monitor/ticket/${savedTicketId}` : undefined;

  // Rendered twice — centered in the empty state, pinned to the bottom of the conversation
  // once messages exist — same pattern CustomsEntryAnalyzer.tsx and bite-import's ChatUI use,
  // so sending the first message visibly moves it from the middle of the card to the bottom.
  const composer = (
    <div className='standard-workflow-composer'>
      {stagedFiles.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1 }}>
          {stagedFiles.map((file, index) => (
            <Stack
              key={`${file.name}-${index}`}
              direction='row'
              spacing={1}
              alignItems='center'
              data-testid='selected-file-item'
              data-file-name={file.name}
              sx={{
                p: 1,
                borderRadius: 2,
                border: '1px solid var(--es-soft-border, var(--border-color))',
                backgroundColor: 'var(--Background-200, var(--bg-colors))',
              }}
            >
              {isImageFile(file) ? (
                <ImageOutlinedIcon fontSize='small' sx={{ color: 'var(--action-color)' }} />
              ) : (
                <DescriptionOutlinedIcon fontSize='small' sx={{ color: 'var(--action-color)' }} />
              )}
              <Typography sx={{ fontWeight: 700, flex: 1, wordBreak: 'break-word' }}>
                {file.name}
              </Typography>
              <Typography variant='caption' sx={{ color: 'var(--text-secondary)' }}>
                {formatFileSize(file.size)}
              </Typography>
              <IconButton
                aria-label={`Remove ${file.name}`}
                onClick={() => handleRemoveStagedFile(index)}
                sx={{ width: 28, height: 28, fontSize: '18px' }}
              >
                ×
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
      <div className='standard-workflow-composer-field'>
        <input
          ref={fileInputRef}
          hidden
          type='file'
          multiple
          accept={ACCEPTED_ATTACHMENT_TYPES}
          onChange={handleFileChange}
          data-testid='export-design-upload-file-input'
        />
        <button
          type='button'
          aria-label='Attach a blueprint, spec PDF, or product photo'
          title='Attach a blueprint, spec PDF, or product photo'
          data-testid='export-design-composer-attach-button'
          onClick={() => fileInputRef.current?.click()}
          disabled={
            !userHasMonitor ||
            isStreaming ||
            isUploadingAttachments ||
            isResuming ||
            startBlockedByCredits
          }
          style={{
            background: 'var(--bg-secondary)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-secondary)',
          }}
        >
          <AddOutlinedIcon fontSize='small' />
        </button>
        <textarea
          ref={composerTextareaRef}
          rows={1}
          value={input}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setInput(event.target.value)}
          onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit(input);
            }
          }}
          placeholder={
            userHasMonitor
              ? 'Describe your product, ask a question, or attach a blueprint / spec sheet / product photo…'
              : 'Upgrade to Business to use the Design Assistant'
          }
          disabled={!userHasMonitor || isStreaming || startBlockedByCredits}
          aria-label='Message'
        />
        <button
          type='button'
          aria-label='Send message'
          onClick={() => submit(input)}
          disabled={
            !userHasMonitor ||
            isStreaming ||
            isUploadingAttachments ||
            isResuming ||
            startBlockedByCredits ||
            (!input.trim() && stagedFiles.length === 0)
          }
        >
          <SendOutlinedIcon fontSize='small' />
        </button>
      </div>
    </div>
  );

  // Leaving discards the live conversation: the design tool's state lives in the pyapi process
  // keyed by clf_id, so nothing survives the navigation except what was saved to a ticket.
  // Once an assessment IS saved and unchanged since, there is nothing to warn about.
  const leaveGuard = useLeaveGuard({
    storageKey: `export-design-leave-warning-optout-${cur_user?.basics?.id || 'anon'}`,
    shouldWarn:
      hasUnsavedWork ||
      Boolean(input.trim()) ||
      stagedFiles.length > 0 ||
      isStreaming,
    onLeave: closeWebSocket,
  });
  leaveAfterSaveRef.current = leaveGuard.handleLeaveAfterSaving;

  const handleSaveBeforeLeaving = () => {
    continueAfterSaveRef.current = true;
    leaveGuard.beginSaveBeforeLeaving();
    handleSaveResult();
  };

  const closeSaveModal = () => {
    continueAfterSaveRef.current = false;
    setIsSaveModalOpen(false);
  };

  return (
    <>
      <Prompt
        when={leaveGuard.shouldBlockNavigation}
        message={leaveGuard.handleBlockedNavigation}
      />
      <LeaveGuardDialog
        open={leaveGuard.isLeaveModalOpen}
        title='Leave Design Assistant?'
        body={
          'Leaving this workflow will discard this conversation — the classification, ' +
          'any design changes, and the uploaded documents. Save the assessment first and ' +
          'it will be waiting for you under Saved Work.'
        }
        onClose={leaveGuard.closeLeaveModal}
        onLeaveWithoutSaving={leaveGuard.handleLeaveWithoutSaving}
        onLeaveAndOptOut={leaveGuard.handleLeaveAndOptOut}
        onSave={hasSavableWork ? handleSaveBeforeLeaving : undefined}
        saveLabel='Save assessment'
      />
      <Modal open={isSaveModalOpen} onClose={closeSaveModal}>
        <CreateTicketWizard
          cur_user={cur_user}
          ticketType={'screening'}
          setFrequency={setFrequency}
          frequency={frequency}
          handleSaveTicket={saveDesignTicket}
          newTicketName={newTicketName}
          setNewTicketName={setNewTicketName}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={savingTicket}
        />
      </Modal>
      <Modal open={isShareModalOpen} onClose={() => setIsShareModalOpen(false)}>
        <AddPersonModal
          cur_user={cur_user}
          handleClose={() => setIsShareModalOpen(false)}
          isOpen={isShareModalOpen}
          sharedArr={shareEmail}
          owner={cur_user?.basics?.email}
          addShared={handleAddShared}
          handleSend={handleSendDesignPdf}
          triggerSend={triggerSendPdf}
        />
      </Modal>
      <WorkflowPageHeader
        title='Design Assistant'
        extraActions={<CreditMeterButton packType='credits' size='sm' />}
        description="Find out where your product falls under ITAR/EAR, which of its specs drive that, and what it means for licensing — in one guided conversation."
        newActionLabel='New Design'
        onNew={startOver}
        saveActionLabel={savedTicketId ? 'Update Saved Result' : 'Save Result'}
        onSave={handleSaveResult}
        savedPath={savedResultPath}
        savedActionLabel='View Result'
        onShare={hasReport ? handleShareResult : undefined}
        disableNew={isStreaming || isUploadingAttachments || isResuming || trialLimitReached}
        disableSave={saveBlocked}
        disableShare={shareBlocked}
      />
      {startBlockedByCredits && (
        <WorkflowCreditWarning
          remaining={remainingCredits}
          cost={DESIGN_ASSISTANT_CREDIT_COST}
        />
      )}
      <div className='standard-workflow-grid export-design-grid'>
      <div className='standard-workflow-assistant-card bill-container'>
        <WorkflowAssistantHeader
          title='Design Assistant'
          status={
            trialLimitReached ? 'unavailable' : isStreaming ? 'running' : 'idle'
          }
        />

        {!userHasMonitor && (
          <RequiredLicense
            userLicense={
              (['none', 'standard', 'pro', 'business', 'enterprise'].includes(
                (cur_user?.license || '').toLowerCase(),
              )
                ? (cur_user?.license || '').toLowerCase()
                : 'none') as LicenseType
            }
            minimumLicense={'business' as LicenseType}
          />
        )}

        <div
          ref={chatScrollRef}
          className='chat-body standard-workflow-chat-scroll standard-workflow-conversation'
        >
          {trialLimitReached ? (
            <TrialRunUsedPanel workflowName='Design Assistant' />
          ) : messages.length === 0 ? (
            <div
              className='standard-workflow-empty-chat'
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                minHeight: '100%',
                boxSizing: 'border-box',
              }}
            >
              {composer}
            </div>
          ) : (
            messages.map((msg, msgIndex) => {
              // A report turn renders its card instead of streamed text, so the reveal
              // callback never fires for it — treat it as ready straight away.
              const trailingReady =
                Boolean(msg.report) || revealedTextIds.has(msg.id);
              // A suggestion the user has already clicked is answered: it leaves this message
              // (and every earlier one that offered it) instead of staying on screen.
              const nextSteps = unansweredNextSteps(
                msg.nextSteps,
                messages
                  .slice(msgIndex + 1)
                  .filter((later) => later.sender === 'user')
                  .map((later) => later.text),
              );
              return (
              <div
                key={msg.id}
                className={`chat-message ${msg.sender}`}
              >
                {msg.sender === 'user' ? (
                  <div className='message-bubble'>
                    {msg.attachedFilenames && msg.attachedFilenames.length > 0 && (
                      <Stack direction='row' spacing={0.5} flexWrap='wrap' sx={{ mb: 0.5 }}>
                        {msg.attachedFilenames.map((name) => (
                          <Stack
                            key={name}
                            direction='row'
                            spacing={0.5}
                            alignItems='center'
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              border: '1px solid var(--es-soft-border, var(--border-color))',
                              backgroundColor: 'var(--Background-200, var(--bg-colors))',
                            }}
                          >
                            <DescriptionOutlinedIcon sx={{ fontSize: 14, color: 'var(--action-color)' }} />
                            <Typography variant='caption' sx={{ wordBreak: 'break-word' }}>
                              {name}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                    {msg.text}
                  </div>
                ) : msg.text === 'Thinking...' ? (
                  <div className='message-bubble thinking'>
                    <div className='standard-workflow-thought-summary standard-workflow-thought-summary-live'>
                      {`Thinking${
                        thinkingElapsed >= 2 ? ` for ${thinkingElapsed}s` : '…'
                      }`}
                    </div>
                    <div className='standard-workflow-thought-details'>
                      {renderThoughtSteps(thoughtStepsRef.current, 'live')}
                      <div className='standard-workflow-thought-step standard-workflow-thought-step-thinking'>
                        <span aria-hidden='true' />
                        <div>Thinking…</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='message-bubble'>
                    {msg.thoughtSteps && msg.thoughtSteps.length > 0 && (
                      <div className='standard-workflow-thought-block'>
                        <button
                          type='button'
                          className='standard-workflow-thought-summary'
                          onClick={() => toggleThought(msg.id)}
                        >
                          {formatThoughtDuration(msg.thoughtSeconds ?? null)}
                          {collapsedThoughts.has(msg.id) ? ' ▸' : ' ▾'}
                        </button>
                        {!collapsedThoughts.has(msg.id) && (
                          <div className='standard-workflow-thought-details'>
                            {renderThoughtSteps(msg.thoughtSteps, `done-${msg.id}`)}
                          </div>
                        )}
                      </div>
                    )}
                    {msg.productSpecsCard && (
                      <ExportDesignPhaseCard
                        {...buildProductSpecsCardProps(msg.productSpecsCard)}
                        defaultExpanded
                      />
                    )}
                    {msg.keySpecsCard && (
                      <ExportDesignPhaseCard
                        {...buildKeySpecsCardProps(msg.keySpecsCard)}
                        defaultExpanded
                      />
                    )}
                    {msg.report ? (
                      <ExportDesignReportCard
                        report={msg.report}
                        onSendToExportWorkflow={handleSendToExportWorkflow}
                        onSaveResult={handleSaveResult}
                        onShareResult={handleShareResult}
                        disableSave={shareBlocked}
                        disableShare={shareBlocked}
                        savedTicketPath={savedResultPath}
                      />
                    ) : (
                      <WorkflowStreamingResponse
                        text={msg.text}
                        animate={
                          msg.sender === 'bot' && msgIndex === messages.length - 1
                        }
                        onComplete={() =>
                          setRevealedTextIds((prev) =>
                            prev.has(msg.id) ? prev : new Set(prev).add(msg.id),
                          )
                        }
                      />
                    )}
                    {msg.continuePrompt && trailingReady && (
                      <div className='export-design-continue-block'>
                        <p className='export-design-continue-prompt'>{msg.continuePrompt}</p>
                        {msgIndex === messages.length - 1 && (
                          <button
                            type='button'
                            className='standard-workflow-primary-btn'
                            onClick={() => submit('yes')}
                            disabled={isStreaming}
                          >
                            Continue
                          </button>
                        )}
                      </div>
                    )}
                    {nextSteps.length > 0 && trailingReady && (
                      <ol className='options-container workflow-control-options'>
                        {nextSteps.map((step) => (
                          <li key={step.label}>
                            <button
                              type='button'
                              className='option-button'
                              onClick={() => submit(step.prompt)}
                              disabled={isStreaming}
                            >
                              {step.label}
                            </button>
                          </li>
                        ))}
                      </ol>
                    )}
                    {msg.disclaimer && trailingReady && (
                      <p className='export-design-turn-disclaimer'>{msg.disclaimer}</p>
                    )}
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>

        {messages.length > 0 && !trialLimitReached && composer}
      </div>

      <div
        className='standard-workflow-progress-card bill-container-fit-screen'
        style={trialLimitReached ? { opacity: 0.55 } : undefined}
      >
        <WorkflowProgressHeader title='Design Progress' />
        <WorkflowProgressTimeline steps={progressSteps} disableExpand />
      </div>
    </div>
    </>
  );
};

export default ExportDesignChatUI;
